/**
 * toast — the single source of transient notifications for the app.
 *
 * Thin wrapper over `sonner` that gives each notification type a sensible
 * default lifetime, so callers don't have to think about it:
 *
 *   • success  — 3.5s  (quick confirmation, get out of the way)
 *   • info      — 4.5s
 *   • warning   — 7s    (lingers; the user should notice)
 *   • error     — 10s   (persists longest; pass { duration: Infinity }
 *                        for critical errors that must be dismissed by hand)
 *
 * The <Toaster> (mounted once in App.js) renders these with rich colors,
 * icons, a close button (manual dismissal), stacking, top-right positioning,
 * an ARIA live region, and pause-on-hover — all built into sonner.
 *
 * Usage is API-compatible with sonner, so existing call sites keep working:
 *   import { toast } from "@/lib/toast";
 *   toast.success("Client saved");
 *   toast.error("Couldn't send the email. Check the address and try again.");
 *   toast.promise(save(), { loading: "Saving…", success: "Saved", error: "…" });
 *
 * Any per-call `duration` still wins over the defaults below.
 */
import { toast as sonnerToast } from "sonner";

export const DURATIONS = {
  success: 3500,
  info: 4500,
  message: 4500,
  warning: 7000,
  error: 10000,
};

const withDefaultDuration = (fn, duration) => (message, opts = {}) =>
  fn(message, { duration, ...opts });

// Preserve sonner's full surface (promise, loading, custom, dismiss, getHistory…)
// and only layer type-appropriate default durations on top.
export const toast = Object.assign(
  (message, opts = {}) => sonnerToast(message, { duration: DURATIONS.message, ...opts }),
  sonnerToast,
  {
    success: withDefaultDuration(sonnerToast.success, DURATIONS.success),
    info: withDefaultDuration(sonnerToast.info, DURATIONS.info),
    message: withDefaultDuration(sonnerToast.message, DURATIONS.message),
    warning: withDefaultDuration(sonnerToast.warning, DURATIONS.warning),
    error: withDefaultDuration(sonnerToast.error, DURATIONS.error),
  }
);

export default toast;
